// The venue settings form (issue #11): name and city, number mode, languages and the default, opening hours,
// paused registrations, the Google Place ID and the two feedback switches; issue #20 adds the venue logo (its own
// upload, see Branding.tsx) and the venue's own message texts, issue #13 the poster's second language and the poster
// downloads (Poster.tsx). One form, one request: it is validated with the core rules before the request and again by
// the API, which answers with the same codes; every refusal is shown in the page language, never as a code. Every
// state carries a symbol or a label, never color alone.
import { useState, type FormEvent } from 'react';
import {
  LANG_LABELS,
  SUPPORTED_LANGS,
  UI,
  cleanName,
  googleReviewUrl,
  hasOpeningHours,
  isMessageOverrideError,
  messageErrorCopy,
  messageProblemCopy,
  messageSample,
  parseLanguages,
  parseOpeningHours,
  parsePlaceId,
  pickDefaultLang,
  type Lang,
  type MessageErrorField,
  type MessageOverrideError,
  type NumberMode,
  type OverrideKind,
  type PosterSize,
} from '@ringring/core';
import { LogoCard, MessagesEditor, changedMessages, firstMessageProblem, initialDrafts, type MessageDrafts } from './Branding';
import { HoursEditor } from './HoursEditor';
import { PosterDownloads, PosterSettings, effectivePosterLang2 } from './Poster';
import { errorText, type ApiError, type Venue, type VenueSettingsBody } from './api';
import { daysFromHours, hoursFromDays, type DayHours } from './hours';

const ERROR_KEYS = ['bad_venue_name', 'bad_city', 'bad_mode', 'bad_languages', 'bad_default_lang', 'bad_hours', 'bad_place_id', 'bad_setting', 'bad_poster_lang'] as const;
type ErrorKey = (typeof ERROR_KEYS)[number];

/** The copy of a settings refusal in the page language, or null for a code the form does not know. */
export function settingsErrorCopy(lang: Lang, code: string | undefined): string | null {
  return (ERROR_KEYS as readonly string[]).includes(code ?? '') ? UI[lang][code as ErrorKey] : null;
}

export interface SettingsFormProps {
  lang: Lang;
  venue: Venue;
  busy: boolean;
  /** True for a moment after a save: the form shows the saved confirmation. */
  saved: boolean;
  /** Sends the body; throws the ApiError on a refusal so the form shows the copy of its code. */
  onSave: (body: VenueSettingsBody) => Promise<void>;
  /** Uploads the logo file at once (issue #20); throws the ApiError on a refusal. */
  onUploadLogo?: (file: File) => Promise<void>;
  onRemoveLogo?: () => Promise<void>;
  /** Fetches and saves the poster PDF of a pickup point (issue #13); throws the ApiError of a refusal. */
  onDownloadPoster?: (pickupPointId: string, size: PosterSize, fileName: string) => Promise<void>;
}

const asyncNoop = async () => undefined;

export function SettingsForm({ lang, venue, busy, saved, onSave, onUploadLogo = asyncNoop, onRemoveLogo = asyncNoop, onDownloadPoster = asyncNoop }: SettingsFormProps) {
  const t = UI[lang];
  const [name, setName] = useState(venue.name);
  const [city, setCity] = useState(venue.city ?? '');
  const [mode, setMode] = useState<NumberMode>(venue.number_mode);
  const [langs, setLangs] = useState<Lang[]>(venue.languages);
  const [defaultLang, setDefaultLang] = useState<Lang>(venue.default_lang);
  const [days, setDays] = useState<DayHours[]>(() => daysFromHours(venue.opening_hours));
  const [paused, setPaused] = useState(venue.registrations_paused);
  const [placeId, setPlaceId] = useState(venue.google_place_id ?? '');
  const [feedback, setFeedback] = useState(venue.feedback_enabled);
  const [googleLink, setGoogleLink] = useState(venue.google_link_enabled);
  const [posterLang2, setPosterLang2] = useState<Lang | null>(venue.poster_lang_2);
  /**
   * The code of the first problem (a local check or the API's refusal), worded at render time in the screen language;
   * a refused message text (issue #20) also keeps which text it is about.
   */
  const [problem, setProblem] = useState<{ code: string; field?: MessageErrorField } | null>(null);
  // The message texts (issue #20): `baseline` is what the venue has stored, so only edited texts are sent.
  const [baseline, setBaseline] = useState<MessageDrafts>(() => initialDrafts(venue.message_overrides));
  const [drafts, setDrafts] = useState<MessageDrafts>(baseline);
  const [messageLang, setMessageLang] = useState<Lang>(venue.default_lang);

  const place = parsePlaceId(placeId);
  // Counted and previewed as a guest of this venue would get them, with the name and the mode as the form has them now.
  const sample = messageSample(
    cleanName(name) ?? venue.name,
    venue.pickup_points.filter((p) => p.archived_at === null).map((p) => p.name),
    mode,
  );
  const setDraft = (l: Lang, k: OverrideKind, text: string) => setDrafts((d) => ({ ...d, [l]: { ...d[l], [k]: text } }));
  /** A refused text: the editor of its language is shown, and the line above the save button names it. */
  const messageProblem = (error: MessageOverrideError, field: MessageErrorField) => {
    if (field.lang) setMessageLang(field.lang);
    setProblem({ code: error, field });
  };
  /** The problem in the screen language: "Français, Message « commande prête » : …" for a text, the code's copy otherwise. */
  const problemText = (p: { code: string; field?: MessageErrorField }): string =>
    isMessageOverrideError(p.code) ? messageProblemCopy(lang, p.field ?? {}, messageErrorCopy(lang, p.code, p.field?.placeholder)) : (settingsErrorCopy(lang, p.code) ?? errorText(lang, p.code));

  const toggleLang = (l: Lang) => {
    const on = langs.includes(l);
    setLangs(on ? langs.filter((x) => x !== l) : SUPPORTED_LANGS.filter((x) => x === l || langs.includes(x)));
  };

  /** The copy key of the first problem, with the same rules the API applies. */
  const check = (): ErrorKey | null => {
    if (!cleanName(name)) return 'bad_venue_name';
    if (!cleanName(city)) return 'bad_city';
    if (!parseLanguages(langs)) return 'bad_languages';
    const h = parseOpeningHours(hoursFromDays(days));
    if (!h || !hasOpeningHours(h)) return 'bad_hours';
    if (place.error) return 'bad_place_id';
    return null;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const bad = check();
    if (bad) {
      setProblem({ code: bad });
      return;
    }
    const badText = firstMessageProblem(baseline, drafts, sample);
    if (badText) {
      messageProblem(badText.error, { lang: badText.lang, kind: badText.kind, placeholder: badText.placeholder });
      return;
    }
    setProblem(null);
    const languages = parseLanguages(langs) ?? [venue.default_lang];
    const messages = changedMessages(baseline, drafts);
    const chosenDefault = pickDefaultLang(languages, defaultLang);
    const body: VenueSettingsBody = {
      name,
      city,
      number_mode: mode,
      languages,
      default_lang: chosenDefault,
      opening_hours: hoursFromDays(days),
      registrations_paused: paused,
      google_place_id: place.value ?? null,
      feedback_enabled: feedback,
      google_link_enabled: googleLink,
      ...(Object.keys(messages).length > 0 ? { messages } : {}),
      // the language shown as chosen; null when the venue has a single language (the poster is then single-language)
      poster_lang_2: effectivePosterLang2(chosenDefault, languages, posterLang2),
    };
    try {
      await onSave(body);
      setBaseline(drafts);
    } catch (err) {
      const apiErr = err as ApiError;
      const d = apiErr.details ?? {};
      if (isMessageOverrideError(apiErr.code)) messageProblem(apiErr.code, { lang: d.message_lang ?? undefined, kind: d.message_kind ?? undefined, placeholder: d.placeholder ?? undefined });
      else setProblem({ code: apiErr.code ?? 'internal' });
    }
  };

  return (
    <form className="settings" onSubmit={submit} noValidate>
      <section className="card" aria-labelledby="s-venue">
        <h2 id="s-venue">{t.venue_details}</h2>
        <label htmlFor="s-name">{t.venue_name}</label>
        <input id="s-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="off" aria-describedby="s-name-hint" />
        <p id="s-name-hint" className="muted">
          {t.venue_name_hint}
        </p>
        <label htmlFor="s-city">{t.city}</label>
        <input id="s-city" value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} autoComplete="address-level2" />
      </section>

      <LogoCard lang={lang} venueName={venue.name} logoUrl={venue.logo_url} onUpload={onUploadLogo} onRemove={onRemoveLogo} />

      <section className="card" aria-labelledby="s-mode">
        <h2 id="s-mode">{t.mode_label}</h2>
        <fieldset className="choices">
          <legend>{t.mode_prompt}</legend>
          {(['scan', 'ticket'] as const).map((m) => (
            <label className={`choice ${mode === m ? 'selected' : ''}`} key={m}>
              <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} />
              <span className="choice-title">
                <span aria-hidden="true">{mode === m ? '● ' : '○ '}</span>
                {m === 'scan' ? t.mode_scan : t.mode_ticket}
              </span>
              <span className="muted">{m === 'scan' ? t.mode_scan_desc : t.mode_ticket_desc}</span>
            </label>
          ))}
        </fieldset>
      </section>

      <section className="card" aria-labelledby="s-langs">
        <h2 id="s-langs">{t.languages_label}</h2>
        <fieldset className="choices inline">
          <legend>{t.languages_prompt}</legend>
          {SUPPORTED_LANGS.map((l) => (
            <label className="check" key={l} lang={l}>
              <input type="checkbox" checked={langs.includes(l)} onChange={() => toggleLang(l)} />
              <span>{LANG_LABELS[l]}</span>
            </label>
          ))}
        </fieldset>
        <p className="muted">{t.languages_hint}</p>
        <fieldset className="choices inline">
          <legend>{t.default_lang}</legend>
          {langs.map((l) => (
            <label className="check" key={l} lang={l}>
              <input type="radio" name="default_lang" value={l} checked={pickDefaultLang(langs, defaultLang) === l} onChange={() => setDefaultLang(l)} />
              <span>{LANG_LABELS[l]}</span>
            </label>
          ))}
        </fieldset>
      </section>

      <MessagesEditor lang={lang} languages={langs.length > 0 ? langs : [venue.default_lang]} active={messageLang} onActive={setMessageLang} drafts={drafts} onChange={setDraft} sample={sample} />

      <PosterSettings lang={lang} defaultLang={pickDefaultLang(langs, defaultLang)} languages={langs} value={posterLang2} onChange={setPosterLang2}>
        {venue.pickup_points
          .filter((p) => p.archived_at === null)
          .map((p) => (
            <div className="poster-pp" key={p.id}>
              <strong>{p.name}</strong>
              <PosterDownloads lang={lang} venueName={venue.name} pp={p} onDownload={(size, fileName) => onDownloadPoster(p.id, size, fileName)} note={t.poster_save_first} />
            </div>
          ))}
      </PosterSettings>

      <section className="card" aria-labelledby="s-hours">
        <h2 id="s-hours">{t.hours_label}</h2>
        <p className="muted">{t.hours_intro}</p>
        <HoursEditor lang={lang} days={days} onChange={setDays} idPrefix="settings-hours" />
      </section>

      <section className="card" aria-labelledby="s-registrations">
        <h2 id="s-registrations">{t.registrations_label}</h2>
        <div className="row">
          <label className="check" htmlFor="s-paused">
            <input id="s-paused" type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
            <span>{t.pause_label}</span>
          </label>
          <span className={`tag ${paused ? 'call' : 'ready'}`} role="status">
            <span aria-hidden="true">{paused ? '⏸' : '✓'}</span> {paused ? t.paused_tag : t.active_tag}
          </span>
        </div>
        <p className="muted">{t.pause_hint}</p>
      </section>

      <section className="card" aria-labelledby="s-feedback">
        <h2 id="s-feedback">{t.feedback_section}</h2>
        <label htmlFor="s-place">{t.place_id_label}</label>
        <input id="s-place" value={placeId} onChange={(e) => setPlaceId(e.target.value)} dir="ltr" autoComplete="off" aria-describedby="s-place-hint" aria-invalid={place.error ? true : undefined} />
        <p id="s-place-hint" className="muted">
          {t.place_id_hint}
        </p>
        {place.value ? (
          <p className="muted">
            {t.review_link_preview}{' '}
            <code>
              <bdi dir="ltr">{googleReviewUrl(place.value)}</bdi>
            </code>
          </p>
        ) : null}
        <div className="row">
          <label className="check" htmlFor="s-feedback-on">
            <input id="s-feedback-on" type="checkbox" checked={feedback} onChange={(e) => setFeedback(e.target.checked)} />
            <span>{t.feedback_label}</span>
          </label>
          <span className="tag">{feedback ? `✓ ${t.on}` : `— ${t.off}`}</span>
        </div>
        <p className="muted">{t.feedback_hint}</p>
        <div className="row">
          <label className="check" htmlFor="s-google-link">
            <input id="s-google-link" type="checkbox" checked={googleLink} onChange={(e) => setGoogleLink(e.target.checked)} />
            <span>{t.google_link_label}</span>
          </label>
          <span className="tag">{googleLink ? `✓ ${t.on}` : `— ${t.off}`}</span>
        </div>
        <p className="muted">{t.google_link_hint}</p>
        {googleLink && !place.value ? (
          <p className="muted" role="status">
            <span aria-hidden="true">⚠</span> {t.google_link_needs_id}
          </p>
        ) : null}
      </section>

      {problem ? (
        <p id="settings-problem" className="error" role="alert">
          <span aria-hidden="true">✕</span> {problemText(problem)}
        </p>
      ) : null}
      <div className="row">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? t.saving : t.save}
        </button>
        {saved ? (
          <span className="tag ready" role="status">
            <span aria-hidden="true">✓</span> {t.settings_saved}
          </span>
        ) : null}
      </div>
    </form>
  );
}
